import React, { useState, useCallback } from 'react';
import {
  SearchBox,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { BoondSearchResult } from '@gridwise/shared';
import { globalSearch } from '../api/search';

const useStyles = makeStyles({
  container: { position: 'relative', width: '100%' },
  results: {
    maxHeight: '300px',
    overflowY: 'auto',
    minWidth: '320px',
  },
  resultItem: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  sublabel: { color: tokens.colorNeutralForeground3 },
  empty: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

interface Props {
  placeholder?: string;
  types?: string[];
  onSelect: (result: BoondSearchResult) => void;
}

export default function BoondSearchInput({ placeholder = 'Rechercher...', types, onSelect }: Props) {
  const styles = useStyles();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BoondSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  const handleChange = useCallback(
    (_: any, data: { value: string }) => {
      const value = data.value;
      setQuery(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.length < 2) {
        setResults([]);
        setOpen(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const res = await globalSearch(value, types);
          setResults(res);
          setOpen(res.length > 0);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 400);
    },
    [types],
  );

  const handleSelect = (result: BoondSearchResult) => {
    setQuery(result.label);
    setOpen(false);
    onSelect(result);
  };

  const typeLabels: Record<string, string> = {
    company: 'Société',
    contact: 'Contact',
    opportunity: 'Opportunité',
    project: 'Projet',
    action: 'Action',
  };

  return (
    <div className={styles.container}>
      <SearchBox
        value={query}
        onChange={handleChange}
        placeholder={placeholder}
        contentAfter={loading ? <Spinner size="tiny" /> : undefined}
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            borderRadius: 4,
            border: '1px solid #e0e0e0',
          }}
        >
          <div className={styles.results}>
            {results.length === 0 ? (
              <div className={styles.empty}>
                <Text size={200}>Aucun résultat</Text>
              </div>
            ) : (
              results.map((r) => (
                <div
                  key={`${r.type}-${r.id}`}
                  className={styles.resultItem}
                  onClick={() => handleSelect(r)}
                >
                  <Text size={300} weight="semibold">{r.label}</Text>
                  <Text size={200} className={styles.sublabel}>
                    {typeLabels[r.type] || r.type}
                    {r.sublabel ? ` · ${r.sublabel}` : ''}
                    {r.manager ? ` · ${r.manager}` : ''}
                  </Text>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
